import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import RADApplication from '@/Management/RADApplication';
import TabStripTab2ViewModelType from '@/ViewModels/Controls/TabStripTab2ViewModel';
import Utilities from '@/Utility/Utilities';

export default class TabStrip2ViewModel extends ControlViewModel {

    private TabStripTab2ViewModel = ComponentManager.getViewModel<typeof TabStripTab2ViewModelType>('TabStripTab2ViewModel');

    /**
    * Enthält die Kindelemente
    */
    public tabs: KnockoutObservableArray<TabStripTab2ViewModelType>;

    /**
    * Bestimmt, ob die Tabs eingeblendet werden sollen (default: true)
    */
    public showTabs: KnockoutObservable<boolean>;

    /**
    * Definiert den Index des selektierten Tabs
    */
    public selectedIndex: KnockoutObservable<number>;

    /**
    * Definiert die Instanz des selektierten Tabs
    */
    public selected: KnockoutObservable<TabStripTab2ViewModelType>;

    /**
    * Bestimmt den aktiven Tab
    */
    public select: KnockoutComputed<number>;

    public isVisible: KnockoutComputed<boolean>;

    public isSortable: KnockoutObservable<boolean>;

    /**
    * Wird ein aktiver Tab unsichtbar geschaltet, so wird automatisch der nachfolgende Tab selektiert
    */
    private tabVisibilitySubscriptions: Array<KnockoutComputed<any>>;

    /**
    * Gibt den Gruppennamen des ersten Eintrags im Container an.
    */
    private firstGroupName: string;

    private isSortingInitialized: boolean;

    /**
     * Das ViewModel für den TabStrip2
     * @param {object} params - Ein Objekt mit Optionen für das Control
     * @constructor
     */
    public constructor(params) {
        super(params);
        this.tabs = this.observableOrDefault(params.tabs, []) as KnockoutObservableArray<TabStripTab2ViewModelType>;
        this.showTabs = this.observableOrDefault(params.showTabs, true);
        this.selectedIndex = this.observableOrDefault(params.selectedIndex, 0);
        this.selected = this.observableOrDefault(params.selected, null);
        this.firstGroupName = this.paramOrDefault(params.firstGroupName, null);
        this.isSortable = this.observableOrDefault(params.isSortable, false);

        this.isVisible = this.defineComputed(() => {
            const tabs = this.tabs();
            const visibleTabs = _.find(tabs, (t) => t.isVisible());
            return (!!visibleTabs);
        });

        this.select = this.definePureComputed({
            read: () => {
                const tabs = this.tabs();

                for (let i = 0; i < tabs.length; i++) {
                    if (tabs[i].isActive() === true) {
                        return i;
                    }
                }

                return 0;
            },
            write: (index) => {
                const tabs = this.tabs();

                for (let i = 0; i < tabs.length; i++) {
                    tabs[i].isActive(i == index);
                }
            }
        });

        // Aktualisiert den selektierten Tab sobald die Tabs aktualisiert oder der selektierte Index verändert wird
        this.defineSubscription(this.selected, (selected) => {
            const tabs = this.tabs();

            for (let i = 0; i < tabs.length; i++) {
                if (tabs[i] === selected) {
                    this.selectedIndex(i);
                }
            }
        });

        // Aktualisiert den selektierten Tab sobald die Tabs aktualisiert oder der selektierte Index verändert wird
        this.defineComputed(() => {
            const tabs = this.tabs();
            const selectedIndex = this.selectedIndex();

            this.select(selectedIndex);
        });

        this.tabVisibilitySubscriptions = [];

        this.setupAutoTabSelection();

        // Invalidiert die Sortierung, wenn neue Tabs hinzugekommen sind
        this.defineSubscription(this.tabs, () => this.isSortingInitialized = false);
    }

    protected setupAutoTabSelection(): void {
        this.defineComputed(() => {
            const tabs = this.tabs();

            for (let i = 0; i < this.tabVisibilitySubscriptions.length; i++) {
                this.tabVisibilitySubscriptions[i].dispose();
            }

            if (tabs.length > 0) {
                for (let i = 0; i < tabs.length; i++) {
                    const tab = tabs[i];
                    const computed = this.handleTabSelection(tab, tabs, i);

                    this.tabVisibilitySubscriptions.push(computed);
                }
            }
        });
    }

    protected handleTabSelection(tab: TabStripTab2ViewModelType, tabs: TabStripTab2ViewModelType[], index: number): KnockoutComputed<void> {
        return this.defineComputed(() => {
            const isActive = tab.isActive();
            const isVisible = tab.isVisible();

            if (isVisible === false && isActive === true) {
                this.selected(tabs[index + 1]);
            }
        });
    }

    public getUISortedTabs(): TabStripTab2ViewModelType[] {
        if (!this.isSortingInitialized) {
            this.updateTabUIIndices();
            this.isSortingInitialized = true;
        }

        const visibleTabs = _.where(this.tabs(), x => x.isVisible());
        return _.sortBy(visibleTabs, x => x.uiIndex);
    }

    public onReordering(oldIndex: number, newIndex: number): void {
        // Anmerkung:
        // Selected bzw.SelectedIndex muss nicht geupdatet werden, da der Index hier sich nur auf die UI bezieht. 
        // Der Selected Index basiert auf dem Index der originalen Liste.
        this.updateTabIndicesByReordering(oldIndex, newIndex);
        this.setUSDSorting();
    }

    private getUSDSortingKey(): string {
        if (!this.firstGroupName)
            return null;

        return `TabGroupBoxSelectedIndex_${this.firstGroupName}`;
    }

    private getUSDSorting(): string[] {
        const key = this.getUSDSortingKey();
        if (!key)
            return null;

        if (!RADApplication.getInstance().user.existUserSpecificData(key))
            return null;

        const value = RADApplication.getInstance().user.getUserSpecificData(key)();
        return value.split(';');
    }

    private setUSDSorting(): void {
        const key = this.getUSDSortingKey();
        if (!key)
            return;

        const tabs = this.getUISortedTabs();
        const names = tabs.map(x => this.getSortingId(x));
        const value = names.join(';');

        RADApplication.getInstance().user.setUserSpecificData(key, value);
    }

    private getSortingId(tab: TabStripTab2ViewModelType): string {
        if (!tab)
            return null;

        const name = tab.technicalName;
        const index = name.lastIndexOf('.');
        if (index === -1)
            return name;

        return name.substring(index + 1);
    }

    private updateTabUIIndices(): void {
        const sortedTabs = this.getUSDSorting();
        const tabs = this.tabs();
        let currentIndex = 0;

        if (sortedTabs) {
            // Aktualisierung des Index anhand der USD.
            for (let sortedTab of sortedTabs) {
                const tab = _.find(tabs, x => this.getSortingId(x) === sortedTab);
                if (tab)
                    tab.uiIndex = currentIndex++;
            }
        }

        // Die nicht berücksichtigten Tabs werden hinten angehangen.
        for (let tab of tabs) {
            if (tab.uiIndex === -1 || Utilities.isNullOrUndefined(tab.uiIndex))
                tab.uiIndex = currentIndex++;
        }
    }

    private updateTabIndicesByReordering(oldIndex: number, newIndex: number): void {
        const tabs = this.getUISortedTabs();

        const triggeringTab = _.find(tabs, x => x.uiIndex === oldIndex);

        if (!triggeringTab)
            return;

        triggeringTab.uiIndex = newIndex;

        // newIndexIsGreater: Von links nach rechts
        const newIndexIsGreater = oldIndex < newIndex;

        for (let tab of tabs) {
            if (tab === triggeringTab)
                continue;

            if (newIndexIsGreater) {
                // Elemente die zwischen der alten und der neuen Position sind, müssen einen nach Links verschoben werden
                if (oldIndex < tab.uiIndex && tab.uiIndex <= newIndex) {
                    tab.uiIndex -= 1;
                }
            }
            else {
                // Elemente die zwischen der alten und der neuen Position sind, müssen einen nach Rechts verschoben werden
                if (newIndex <= tab.uiIndex && tab.uiIndex < oldIndex) {
                    tab.uiIndex += 1;
                }
            }
        }
    }
}

ComponentManager.setViewModel('TabStrip2ViewModel', TabStrip2ViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');